<template>
    <div
        class="image-grid-viewer d-flex flex-wrap"
        :class="{ 'gap-6': gap === null, 'is-flush': flush }"
        :style="gap === null ? undefined : { gap }"
    >
        <figure
            v-for="item in modelValue"
            :key="`image-grid-viewer-item-${item.id}`"
            class="mb-2 fit-w-min-content fit-h-min-content position-relative"
            :class="{
                'd-none': deletedList.has(`${item.id}`)
            }"
        >
            <v-img
                :src="item.url"
                :width="size"
                :height="size"
                :class="{ 'cursor-pointer': zoomable }"
                @click="onZoom(item)"
                cover
                rounded
            />
            <slot
                name="image-extra"
                :item="item"
            ></slot>
            <figcaption
                v-if="item.title"
                class="text-body-medium"
            >
                {{ item.title }}
            </figcaption>
        </figure>

        <!-- Teleports to the overlay container, so it is not a flex item here. -->
        <v-dialog
            v-if="zoomable"
            v-model="isZoomed"
            width="auto"
            max-width="92vw"
        >
            <!--
                A native <img> rather than <v-img>: the dialog sizes to its
                content and v-img sizes to its container, so the pair resolves
                to zero height and the overlay appears empty.
            -->
            <img
                v-if="zoomedItem"
                :src="zoomedItem.url"
                :alt="zoomedItem.title"
                class="image-grid-viewer-zoom cursor-pointer d-block"
                @click="isZoomed = false"
            />
        </v-dialog>
    </div>
</template>

<script lang="ts" setup>
    import type {
        ImageGridItem
    } from '~/types';


    const props = withDefaults(
        defineProps<{
            modelValue: ImageGridItem[];
            deletedList?: Set<string>;
            // Thumbnail edge length. The figure itself is sized by its contents
            // (`fit-w-min-content` is `!important`), so this is what controls it.
            size?: number;
            // CSS gap between thumbnails. Null keeps the `gap-6` utility class.
            gap?: Nullable<string>;
            // <figure> carries a 40px inline margin from the UA stylesheet. Drop
            // it where the grid has to sit flush with surrounding text.
            flush?: boolean;
            zoomable?: boolean;
        }>(),
        {
            deletedList: () => new Set<string>(),
            size: 180,
            gap: null,
            flush: false,
            zoomable: false
        }
    );

    const isZoomed = ref(false);
    const zoomedItem = ref<Nullable<ImageGridItem>>(null);

    function onZoom(item: ImageGridItem): void {
        if (!props.zoomable) {
            return;
        }

        zoomedItem.value = item;
        isZoomed.value = true;
    }
</script>

<style lang="scss">
.image-grid-viewer {
    figure {
        width: 180px;
    }

    &.is-flush figure {
        margin: 0;
    }
}

// Teleported out of .image-grid-viewer into the overlay container, so this
// cannot be nested above.
.image-grid-viewer-zoom {
    max-width: 92vw;
    max-height: 88vh;
    width: auto;
    height: auto;
    object-fit: contain;
}
</style>
